/**
 * Wrapper around AI SDK `lastAssistantMessageIsCompleteWithToolCalls` that:
 * - Requires at least one assistant message
 * - Trims trailing non-assistant messages before calling the SDK (otherwise the helper returns false
 *   when the UI appends a user row after tool output)
 */

import { lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';

/** Strips trailing user/system rows so the SDK sees an assistant last (post-tool UI queue). */
function messagesForAssistantToolCompleteCheck(messages: readonly unknown[]): unknown[] {
  let end = messages.length;
  while (end > 0) {
    const m = messages[end - 1];
    if (m && typeof m === 'object' && (m as Record<string, unknown>).role === 'assistant') {
      break;
    }
    end--;
  }
  return messages.slice(0, end) as unknown[];
}

export function safeLastAssistantMessageIsCompleteWithToolCalls({
  messages,
}: {
  messages: readonly unknown[] | undefined;
}): boolean {
  if (!Array.isArray(messages) || messages.length === 0) return false;

  const hasAssistant = messages.some((m) => {
    if (!m || typeof m !== 'object') return false;
    return (m as Record<string, unknown>).role === 'assistant';
  });
  if (!hasAssistant) return false;

  const forSdk = messagesForAssistantToolCompleteCheck(messages);
  if (forSdk.length === 0) return false;

  try {
    return lastAssistantMessageIsCompleteWithToolCalls({
      messages: forSdk as UIMessage[],
    });
  } catch {
    return false;
  }
}
